import React, { ReactNode, useEffect } from 'react'
import { Div, Span, onClick } from './html'
import './sysconfig.css'

export type TSysConfigTab = 'preferences' | 'midi' | 'devices' | 'backup'

const TABS: { id: TSysConfigTab; label: string }[] = [
    { id: 'preferences', label: 'PREFERENCES' },
    { id: 'midi', label: 'MIDI' },
    { id: 'devices', label: 'DEVICES' },
    { id: 'backup', label: 'BACKUP' },
]

interface ISystemConfigProps {
    tab: TSysConfigTab
    setTab: (tab: TSysConfigTab) => void
    close: () => void
    // per-tab content; tabs without an entry show a placeholder
    tabs?: Partial<Record<TSysConfigTab, ReactNode>>
}

export function SystemConfig ({ tab, setTab, close, tabs }: ISystemConfigProps) {

    // ESC closes the dialog
    useEffect(() => {
        const handleESC = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                close()
                e.stopPropagation()
                e.preventDefault()
            }
        }
        document.addEventListener('keydown', handleESC)
        return () => document.removeEventListener('keydown', handleESC)
    }, [close])

    const active = TABS.find(_ => _.id === tab) ?? TABS[0]

    return (
        // backdrop click closes; clicks inside the dialog stop propagation
        <Div k='modal' {...onClick(close)}>
            <Div k={['system-config', 'dialog']} {...onClick(undefined)}>
                <Div k='titlebar'>SYSTEM CONFIGURATION
                    <Div k='dismiss' {...onClick(close)}>close</Div>
                </Div>
                <Div k='tabs'>
                    {TABS.map(_ =>
                        <Div
                            key={_.id}
                            k={['tab', _.id === tab && 'active']}
                            {...onClick(() => setTab(_.id))}
                        >
                            {_.label}
                        </Div>
                    )}
                </Div>
                <Div k='body'>
                    {tabs?.[tab] ?? <Div k='placeholder'>This is the {active.label} tab</Div>}
                </Div>
                <Div k='actions'>
                    <Div k={['cta', 'primary']} {...onClick(close)}>CLOSE</Div>
                </Div>
            </Div>
        </Div>
    )
}

// CSV columns written/read by the device import/export (devices.ts `Fields`).
// The first four are required in the header row.
const CSV_REQUIRED = ['DEVICE', 'NODE', 'NODE TYPE', 'NODE MODE']
const CSV_OPTIONAL = [
    'MFR', 'PRODUCT', 'PORT L FROM', 'PORT R FROM', 'PORT L TO', 'PORT R TO',
    'NODE TAGS', 'DEVICE NOTES', 'DEVICE ID', 'NODE ID',
]

/**
 * DEVICES tab content for the System Configuration dialog.
 * Help text + Export / Import actions (wired by the caller).
 */
export function DevicesConfig (
    { onExport, onImport }: { onExport: () => void; onImport: () => void }
) {
    return (
        <Div k='devices-tab'>
            <Div k='devices-pane'>

                <Div k='block tight'>
                    <Div k='section-label'>DEVICES</Div>
                    <Div k='help'>
                        A <b>device</b> is one piece of gear in your rig — its manufacturer, product,
                        and the input/output <b>nodes</b> (physical ports used to patch it). Your
                        <b> device library</b> is the catalog the app draws from when you build chains.
                        <b> Import</b> and <b>Export</b> move that whole library in and out as one
                        spreadsheet-friendly <code>.csv</code> — to back it up, share it between
                        workspaces, or bulk-edit in a spreadsheet and bring it back.
                    </Div>
                </Div>

                <Div k='cards'>
                    <Div k='card'>
                        <Div k='section-label'>EXPORT</Div>
                        <Div k='help'>
                            Saves your <b>complete device library</b> to a CSV — one row per node, with
                            routing (<code>PORT L/R FROM/TO</code>), tags, notes, and stable
                            <code> DEVICE ID</code> / <code>NODE ID</code>. You choose where to save
                            (default <code>pp-device-export.csv</code>).
                        </Div>
                        <Div k='action-row'>
                            <Div k='cta' {...onClick(onExport)}>
                                <Span k='symbols'>download</Span>EXPORT DEVICES…
                            </Div>
                        </Div>
                    </Div>

                    <Div k='card'>
                        <Div k='section-label'>IMPORT</Div>
                        <Div k='help'>
                            Loads a library from a CSV and replaces your current one. Header must include
                            <code> DEVICE</code>, <code>NODE</code>, <code>NODE TYPE</code>,
                            <code> NODE MODE</code>; bad rows are rejected with line numbers.
                        </Div>
                        <Div k='action-row'>
                            <Div k={['cta','danger']} {...onClick(onImport)}>
                                <Span k='symbols'>upload</Span>IMPORT DEVICES…
                            </Div>
                        </Div>
                    </Div>
                </Div>

                <Div k='warn'>
                    <Span k='symbols'>warning</Span>
                    <Div k='txt'>
                        <b>Import is destructive.</b> It <b>overwrites every device</b> and
                        <b> clears all chains</b> in this workspace — can't be undone, so export a
                        backup first. You'll be asked to confirm.
                    </Div>
                </Div>

                <details>
                    <summary>CSV COLUMN REFERENCE</summary>
                    <Div k='cols'>
                        {CSV_REQUIRED.map(c => <Span key={c} k={['col','req']}>{c}</Span>)}
                        {CSV_OPTIONAL.map(c => <Span key={c} k='col'>{c}</Span>)}
                    </Div>
                    <Div k='cols-note'>
                        <Span k='req-swatch'/> required &nbsp;·&nbsp; column order is flexible; extra
                        columns are ignored.
                    </Div>
                </details>

            </Div>
        </Div>
    )
}

/**
 * BACKUP tab content for the System Configuration dialog.
 * Help text + Save / Restore actions (wired by the caller).
 */
export function BackupConfig (
    { onSave, onRestore }: { onSave: () => void; onRestore: () => void }
) {
    return (
        <Div k='backup-tab'>
            <Div k='backup-pane'>

                <Div k='block tight'>
                    <Div k='section-label'>BACKUP</Div>
                    <Div k='help'>
                        A <b>backup</b> is a complete snapshot of your workspace — every <b>device</b>,
                        every <b>chain</b>, and all of your <b>sessions</b> — saved to a single
                        <code> .propatchb</code> file. Use it to safeguard your work before risky
                        changes, move a full setup to another machine, or roll back to a known-good
                        state.
                    </Div>
                </Div>

                <Div k='cards'>
                    <Div k='card'>
                        <Div k='section-label'>SAVE BACKUP</Div>
                        <Div k='help'>
                            Writes your <b>entire workspace</b> — devices, chains, and all saved
                            sessions (plus the active one) — to a <code>.propatchb</code> file. You
                            choose where to save it (default <code>propatch-backup.propatchb</code>).
                            Keep it somewhere safe; it's your full restore point.
                        </Div>
                        <Div k='action-row'>
                            <Div k='cta' {...onClick(onSave)}>
                                <Span k='symbols'>save</Span>SAVE BACKUP…
                            </Div>
                        </Div>
                    </Div>

                    <Div k='card'>
                        <Div k='section-label'>RESTORE</Div>
                        <Div k='help'>
                            Replaces your current workspace with the contents of a
                            <code> .propatchb</code> backup file. Pick a backup you saved earlier;
                            everything in it is loaded as-is.
                        </Div>
                        <Div k='action-row'>
                            <Div k={['cta','danger']} {...onClick(onRestore)}>
                                <Span k='symbols'>settings_backup_restore</Span>RESTORE BACKUP…
                            </Div>
                        </Div>
                    </Div>
                </Div>

                <Div k='warn'>
                    <Span k='symbols'>warning</Span>
                    <Div k='txt'>
                        <b>Restore is destructive.</b> It replaces <b>every device, chain, and
                        session</b> in this workspace with the backup's contents — can't be undone, so
                        save a fresh backup first. You'll be asked to confirm.
                    </Div>
                </Div>

                <details>
                    <summary>WHAT'S IN A BACKUP</summary>
                    <Div k='items'>
                        <Span k='item'><Span k='symbols'>memory</Span>Devices</Span>
                        <Span k='item'><Span k='symbols'>flowchart</Span>Chains</Span>
                        <Span k='item'><Span k='symbols'>dataset</Span>Saved sessions</Span>
                        <Span k='item'><Span k='symbols'>play_circle</Span>Active session</Span>
                    </Div>
                    <Div k='items-note'>
                        Everything is stored in one <code>.propatchb</code> file (JSON). Restoring
                        brings back all of the above together.
                    </Div>
                </details>

            </Div>
        </Div>
    )
}

// the custom node/processor glyph (mirrors widgets.tsx NodeIcon), inlined to keep
// this module free of cross-imports
const NodeGlyph = () => (
    <svg className='nodeicon' viewBox='0 0 100 100' fill='currentColor'>
        <g transform='scale(5) translate(0,3)'>
            <rect x='5' y='12' width='1' height='2'/>
            <rect x='14' y='12' width='1' height='2'/>
            <rect x='5' y='0' width='1' height='2'/>
            <rect x='14' y='0' width='1' height='2'/>
            <path fillRule='evenodd' clipRule='evenodd' d='M0 2C0 1.44772 0.447715 1 1 1H4V3H7V1H13V3H16V1H19C19.5523 1 20 1.44772 20 2V12C20 12.5523 19.5523 13 19 13H16V11H13V13H7V11H4V13H1C0.447715 13 0 12.5523 0 12V2Z'/>
        </g>
    </svg>
)

/**
 * Collapsible reference for the MIDI tab: explains which MIDI settings are saved
 * at the machine/install level vs. travel with the session, plus a legend for the
 * assignment category icons.
 */
export function MidiStorageReference () {
    return (
        <details className='storage-ref'>
            <summary>WHERE THESE SETTINGS ARE SAVED</summary>

            <Div k='intro'>
                MIDI settings on this tab live at two levels — some belong to this{' '}
                <b style={{color:'#e7b766'}}>machine / install</b>, the rest travel with the{' '}
                <b style={{color:'#6fc3a6'}}>session</b> (and are included in backups).
            </Div>

            <table className='lvl-table'>
                <thead>
                    <tr><th>Setting</th><th>Saved at</th><th>Travels with</th></tr>
                </thead>
                <tbody>
                    <tr><td>Listening ports</td><td><span className='badge machine'>MACHINE</span></td><td>this install</td></tr>
                    <tr><td>MUTE binding</td><td><span className='badge machine'>MACHINE</span></td><td>this install</td></tr>
                    <tr><td>Chain / node / AB-group notes</td><td><span className='badge session'>SESSION</span></td><td>session &amp; backups</td></tr>
                    <tr><td>Session activation note</td><td><span className='badge session'>SESSION</span></td><td>session &amp; backups</td></tr>
                </tbody>
            </table>

            <Div k='legend-title'>Assignment category icons</Div>
            <Div k='legend'>
                <Div k='row'>
                    <Span k='ico'><NodeGlyph/></Span>
                    <Span k='lbl'>Node</Span><Span k='desc'>a device node / processor parameter trigger</Span>
                    <span className='badge session'>SESSION</span>
                </Div>
                <Div k='row'>
                    <Span k='ico'><Span k='symbols'>mediation</Span></Span>
                    <Span k='lbl'>AB-group</Span><Span k='desc'>an A/B group trigger</Span>
                    <span className='badge session'>SESSION</span>
                </Div>
                <Div k='row'>
                    <Span k='ico'><Span k='symbols'>flowchart</Span></Span>
                    <Span k='lbl'>Chain</Span><Span k='desc'>activates a chain</Span>
                    <span className='badge session'>SESSION</span>
                </Div>
                <Div k='row'>
                    <Span k='ico'><Span k='symbols'>dataset</Span></Span>
                    <Span k='lbl'>Session</Span><Span k='desc'>loads a saved session</Span>
                    <span className='badge session'>SESSION</span>
                </Div>
                <Div k='row'>
                    <Span k='ico'><Span k='symbols'>settings</Span></Span>
                    <Span k='lbl'>System</Span><Span k='desc'>the global MUTE binding</Span>
                    <span className='badge machine'>MACHINE</span>
                </Div>
            </Div>
        </details>
    )
}
